/**
 * ClientReaction — TASK-687. The pre-call pre-mortem.
 *
 * Every other client surface in Kielis explains the past. This one answers: before you send
 * this, how will they react, and why?
 *
 * The design rule that governs every line below: **this is not a personality read.** It shows
 * POSITIONS THE CLIENT STATED, quoted, with a locator back to where they said it. There is no
 * trait language, no "clients like this", and no driver without a quote — the backend drops
 * uncited claims before they reach here, and when nothing is citable this widget says
 * "not enough documented basis" instead of filling the gap.
 *
 * Client-scoped: receives only `{ clientId }`, server-injected from focus.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, MessageSquare, Quote, ThumbsUp } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetContainer } from '../WidgetContainer';
import { WidgetErrorState } from '../../ui/WidgetErrorState';

type Stance = 'receptive' | 'cautious' | 'resistant' | 'insufficient_basis';

interface ReactionDriver {
  category: string;
  tag?: string | null;
  text?: string | null;
  direction: number;
  observation_count: number;
  /** The sentence the client said. Guaranteed present — uncited drivers never arrive. */
  excerpt: string;
  locator?: Record<string, unknown> | null;
  origin?: string | null;
}

interface ReactionObjection {
  objection: string;
  basis: string;
  kind: string;
}

interface ReactionResponse {
  stance: Stance;
  score: number;
  drivers: ReactionDriver[];
  objections: ReactionObjection[];
  uncited_claims: number;
  basis: string;
  risk_profile?: string | null;
}

const STANCE_STYLE: Record<Stance, { label: string; chip: string; icon: JSX.Element }> = {
  receptive: {
    label: 'Likely receptive',
    chip: 'bg-green-50 text-green-700 border-green-200',
    icon: <ThumbsUp className="h-4 w-4 text-green-600" />,
  },
  cautious: {
    label: 'Likely cautious',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    icon: <AlertTriangle className="h-4 w-4 text-amber-600" />,
  },
  resistant: {
    label: 'Likely resistant',
    chip: 'bg-red-50 text-red-700 border-red-200',
    icon: <AlertTriangle className="h-4 w-4 text-red-600" />,
  },
  insufficient_basis: {
    label: 'Not enough documented basis',
    chip: 'bg-gray-50 text-gray-600 border-gray-200',
    icon: <MessageSquare className="h-4 w-4 text-gray-400" />,
  },
};

/** A locator rendered for a human: "transcript line 118", "page 2". */
function locatorLabel(locator?: Record<string, unknown> | null): string | null {
  if (!locator) return null;
  if (locator.transcript_line != null) return `transcript line ${locator.transcript_line}`;
  if (locator.page != null) {
    return locator.line != null ? `page ${locator.page}, line ${locator.line}` : `page ${locator.page}`;
  }
  return null;
}

export interface ClientReactionProps {
  clientId: string;
  /** What to pre-mortem. Defaults to a general read of the client's stated positions. */
  subject?: string;
  title?: string;
}

export function ClientReaction({ clientId, subject, title }: ClientReactionProps) {
  const [draftSubject, setDraftSubject] = useState(subject ?? '');
  const [askedSubject, setAskedSubject] = useState(subject ?? '');

  const query = useQuery({
    queryKey: ['client-reaction', clientId, askedSubject],
    queryFn: () =>
      httpClient.get<ReactionResponse>(
        `/api/knowledge-graph/${clientId}/reaction`,
        { params: { subject: askedSubject } },
      ),
    // Nothing to pre-mortem yet — do not fire a request for an empty subject.
    enabled: Boolean(clientId) && askedSubject.trim().length >= 3,
  });

  const data = query.data;
  const stance: Stance = data?.stance ?? 'insufficient_basis';
  const style = STANCE_STYLE[stance] ?? STANCE_STYLE.insufficient_basis;

  return (
    <WidgetContainer title={title ?? 'Before you send this'} source="client knowledge graph">
      <div className="space-y-3">
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setAskedSubject(draftSubject.trim());
          }}
        >
          <input
            aria-label="What do you want to pre-mortem?"
            data-testid="reaction-subject-input"
            value={draftSubject}
            onChange={(e) => setDraftSubject(e.target.value)}
            placeholder="e.g. add a luxury goods holding"
            className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
          <button
            type="submit"
            disabled={draftSubject.trim().length < 3}
            className="rounded-md bg-[#002B45] px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            Pre-mortem
          </button>
        </form>

        {askedSubject.trim().length < 3 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            Describe what you are about to put to this client.
          </p>
        ) : query.isLoading ? (
          <div className="py-6 text-center">
            <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-[#002B45] border-t-transparent" />
          </div>
        ) : query.isError ? (
          <WidgetErrorState
            title="Couldn't read the client's recorded positions"
            detail="The knowledge graph didn't respond."
            onRetry={() => void query.refetch()}
          />
        ) : (
          <>
            <div
              data-testid="reaction-stance"
              className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm font-medium ${style.chip}`}
            >
              {style.icon}
              <span>{style.label}</span>
            </div>

            {stance === 'insufficient_basis' ? (
              // The honest empty state. NOT a generic personality read — the whole point.
              <div data-testid="reaction-insufficient" className="space-y-1 text-sm text-muted-foreground">
                <p>
                  Nothing this client has said on record bears on that. No prediction is offered
                  rather than a guess.
                </p>
                {!!data?.uncited_claims && (
                  <p className="text-xs">
                    {data.uncited_claims} recorded position(s) carry no source quote, so they were
                    not used.
                  </p>
                )}
              </div>
            ) : (
              <>
                <div>
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    Because they told you
                  </p>
                  <ul className="space-y-1.5" data-testid="reaction-drivers">
                    {data!.drivers.map((driver, i) => (
                      <li
                        key={`${driver.tag}-${i}`}
                        data-testid="reaction-driver"
                        className="rounded-md border border-border bg-muted/40 px-2.5 py-2"
                      >
                        <div className="flex items-start gap-2">
                          <Quote className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                          {/* The quote IS the explanation — this is the provenance chain. */}
                          <p className="min-w-0 flex-1 text-[13px] italic text-foreground">
                            “{driver.excerpt}”
                          </p>
                        </div>
                        <div className="mt-1 flex flex-wrap items-center gap-2 pl-5 text-[11px] text-muted-foreground">
                          {driver.tag && (
                            <span className="rounded bg-background px-1.5 py-0.5 font-medium">
                              {driver.tag}
                            </span>
                          )}
                          <span>{driver.category.replace('_', ' ')}</span>
                          {/* Corroboration, stated rather than implied (TASK-682). */}
                          <span data-testid="reaction-driver-observations">
                            said {driver.observation_count}×
                          </span>
                          {locatorLabel(driver.locator) && <span>· {locatorLabel(driver.locator)}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                {!!data!.objections.length && (
                  <div>
                    <p className="mb-1 text-xs font-medium text-muted-foreground">
                      Expect to hear
                    </p>
                    <ul className="space-y-1" data-testid="reaction-objections">
                      {data!.objections.map((objection, i) => (
                        <li
                          key={i}
                          data-testid="reaction-objection"
                          className="rounded-md border border-border px-2.5 py-1.5 text-[13px]"
                        >
                          <span className="font-medium text-foreground">“{objection.objection}”</span>
                          <span className="ml-1 text-[11px] text-muted-foreground">
                            — {objection.basis}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <p className="text-[11px] text-muted-foreground">
                  {data!.basis}. Based only on positions this client stated — not on inferred
                  traits.
                </p>
              </>
            )}
          </>
        )}
      </div>
    </WidgetContainer>
  );
}

export default ClientReaction;
